type Options = { alpha?: number; format?: 'array' | 'css' | 'object' };
type RgbaObject = { alpha: number; blue: number; green: number; red: number };
type RgbaTuple = [red: number, green: number, blue: number, alpha: number];
type HexTypes = 'array' | 'css' | 'object';
type HexRgb<T extends HexTypes> = T extends 'css' ? string : T extends 'array' ? RgbaTuple : RgbaObject;

const HEX_CHARACTERS = 'a-f\\d';
const MATCH_3_OR_4_HEX = `#?[${HEX_CHARACTERS}]{3}[${HEX_CHARACTERS}]?`;
const MATCH_6_OR_8_HEX = `#?[${HEX_CHARACTERS}]{6}([${HEX_CHARACTERS}]{2})?`;
const NON_HEX_CHARS = new RegExp(`[^#${HEX_CHARACTERS}]`, 'gi');
const VALID_HEX_SIZE = new RegExp(`^${MATCH_3_OR_4_HEX}$|^${MATCH_6_OR_8_HEX}$`, 'i');

export const hexToRgbaAsCss = (hex: string, alpha: number): string => {
    return hexRgb(hex, { alpha, format: 'css' });
};

export const hexToRgbAsCss = (hex: string, chroma: number): string => {
    const rgbaObject = hexRgb(hex, { format: 'object' });

    const { blue, green, red } = rgbaObject;

    const rgbObject = {
        blue: blue * chroma + 255 * (1 - chroma),
        green: green * chroma + 255 * (1 - chroma),
        red: red * chroma + 255 * (1 - chroma),
    };

    return `rgb(${rgbObject.red}, ${rgbObject.green}, ${rgbObject.blue})`;
};

const hexRgb = <T extends HexTypes>(hex: string, options: Options & { format: T }): HexRgb<T> => {
    if (NON_HEX_CHARS.test(hex) || !VALID_HEX_SIZE.test(hex)) {
        throw new TypeError('Expected a valid hex string');
    }

    const hexWithoutPound = hex.replace(/^#/, '');
    const defaultAlpha = 1;

    if (hexWithoutPound.length === 8) {
        return convertHexToRgb(
            hexWithoutPound.slice(0, 6),
            Number.parseInt(hexWithoutPound.slice(6, 8), 16) / 255,
            options,
        );
    }

    if (hexWithoutPound.length === 4) {
        return convertHexToRgb(
            hexWithoutPound.slice(0, 3),
            Number.parseInt(hexWithoutPound.slice(3, 4).repeat(2), 16) / 255,
            options,
        );
    }

    if (hexWithoutPound.length === 3) {
        const [red, green, blue] = hexWithoutPound.split('');
        return convertHexToRgb(`${red}${green}${blue}`.slice(0, 3), defaultAlpha, options);
    }

    return convertHexToRgb(hexWithoutPound, defaultAlpha, options);
};

const convertHexToRgb = <T extends HexTypes>(
    hex: string,
    alphaFromHex: number,
    options: Options & { format: T },
): HexRgb<T> => {
    const red = Number.parseInt(hex.slice(0, 2), 16);
    const green = Number.parseInt(hex.slice(2, 4), 16);
    const blue = Number.parseInt(hex.slice(4, 6), 16);

    const alpha = options.alpha ?? alphaFromHex;

    if (options.format === 'array') {
        return [red, green, blue, alpha] as HexRgb<T>;
    }

    if (options.format === 'css') {
        const alphaString = alpha === 1 ? '' : ` / ${Number((alpha * 100).toFixed(2))}%`;
        return `rgb(${red} ${green} ${blue}${alphaString})` as HexRgb<T>;
    }

    return { alpha, blue, green, red } as HexRgb<T>;
};
